/*
 *
 *  * Copyright (c) 2020 General Electric Company. All rights reserved.
 *  *
 *  * The copyright to the computer software herein is the property of
 *  * General Electric Company. The software may be used and/or copied only
 *  * with the written permission of General Electric Company or in accordance
 *  * with the terms and conditions stipulated in the agreement/contract
 *  * under which the software has been supplied.
 *
 */
import * as _ from 'underscore';
import { Logger, MessageService } from '@core/lib/services';
import { ProxyDataService } from '@app/core/http';

export class ClinicalCacheFactory {
  private readonly cache: Map<number, any>;
  private listeners: any = {};
  refreshScenario = false;

  constructor(
    private name: string,
    private endpoint: (id, params) => string,
    private comparator,
    private log: Logger,
    private messageService: MessageService,
    private dataService: ProxyDataService
  ) {
    this.cache = new Map<number, any>();
  }

  getCache(): Map<number, any> {
    return this.cache;
  }

  get(id, refreshIfCached, sirccRequestParams?) {
    const cachedData = this.cache.get(id);

    if (cachedData) {
      if (refreshIfCached == false) {
        return Promise.resolve(cachedData);
      } else if (refreshIfCached == true && !this.refreshScenario) {
        this.refresh(id, sirccRequestParams);
        return Promise.resolve(cachedData);
      } else {
        this.refreshScenario = false;
        this.cache.delete(id);
      }
    }

    return new Promise((resolve, reject) => {
      this.dataService
        .get(this.endpoint(id, sirccRequestParams))
        .then((data: any) => {
          this.updateCache(id, data);
          resolve(data);
        })
        .catch(reason => {
          this.log.error('Error Occurred while getting clinical info, ERROR - ', JSON.stringify(reason));
          this.messageService.broadcast('refreshFailed', id);
          reject(reason);
        });
    });
  }

  updateCache(id, serverData) {
    this.cache.set(id, serverData);
    this.messageService.broadcast('clinicalInfo', serverData);
  }

  refresh(id, sirccRequestParams) {
    const cachedData = this.cache.get(id);
    const channelName = ['clinicalInfo.refresh', this.name];

    this.dataService
      .get(this.endpoint(id, sirccRequestParams))
      .then(serverData => {
        this.comparator(id, cachedData, serverData);
        channelName.push('success');
      })
      .catch(error => {
        this.log.error('cacheManager - .refresh() - failed to refresh the cache because: ' + JSON.stringify(error));
        channelName.push('failure');
      })
      .finally(() => {
        this.broadcastResult(channelName);
      });
  }

  updateSingleFieldInCache(data) {
    const cachedData = this.cache.get(data.requestedProcedureId)
      ? this.cache.get(data.requestedProcedureId).requestedProcedure
      : void 0;
    if (!cachedData) {
      return;
    } else {
      _.extend(cachedData, _.pick(data, _.keys(cachedData)));
    }
  }

  getRemoteStudyCache(id) {
    const cachedData = this.cache.get(id);
    if (cachedData) {
      return cachedData;
    }
  }

  remove(id) {
    this.cache.delete(id);
  }

  on(event, listener, context) {
    if (!_.isFunction(listener)) {
      this.log.error('client-cache-factory: .on() - listener is not a function');
      return;
    }

    listener._context = context || null;

    if (this.listeners[event]) {
      this.listeners[event].push(listener);
    } else {
      this.listeners[event] = [listener];
    }

    return {
      unregister: () => {
        const index = this.listeners[event].indexOf(listener);
        this.listeners[event].splice(index, 1);
      }
    };
  }

  fireEvent(eventName, requestedProcedureId, priorityId = undefined, priority = undefined) {
    const eventListeners = this.listeners[eventName];
    if (!eventListeners) {
      this.log.error('cache-factory: .fireEvent() - no listeners have been registered, please check eventName');
    } else {
      _.each(eventListeners, listener => {
        listener.apply(listener._context, [requestedProcedureId, priorityId, priority]);
      });
    }
  }

  updateWorkItemState(id, state) {
    const cachedData = this.cache.get(id);
    if (cachedData && state) {
      cachedData.examStates = state;
    }
  }

  getComparator() {
    return this.comparator;
  }

  private broadcastResult(channelName: string[]) {
    this.messageService.broadcast(channelName.join('.'));
  }
}
